import { useMutation, useQuery } from "react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import PlanChangeService, {
  BillingAddress,
  PlanChange,
  PlanChangePreview,
  PlanChangeResult,
  planChangeErrorMessage,
  planChangeRefusal,
} from "../../services/planChange";
import i18next from "i18next";
import { queryClient } from "../../providers/QueryProvider";
import { forgetOwnerPlans } from ".";

const PLAN_CHANGES_KEY = "plan-changes";

const refusalToast = (err: AxiosError<any>) => {
  const reason = planChangeRefusal(err);
  return reason
    ? i18next.t(`dashboard.billings.planChange.refusals.${reason}`)
    : planChangeErrorMessage(err) || err.message;
};

export const forgetPlanChanges = () =>
  queryClient.invalidateQueries(PLAN_CHANGES_KEY);

type Scope = { customerId: string; entityId: string };

export const usePlanChanges = (
  customerId: string,
  entityId: string,
  enabled = true
) =>
  useQuery<PlanChange[], AxiosError<any>>(
    [PLAN_CHANGES_KEY, entityId],
    () => PlanChangeService.list(customerId, entityId),
    {
      enabled: enabled && Boolean(customerId) && Boolean(entityId),
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );

export const usePreviewPlanChange = () =>
  useMutation<
    PlanChangePreview,
    AxiosError<any>,
    Scope & { from_plan: string; to_plan: string }
  >(({ customerId, entityId, ...body }) =>
    PlanChangeService.preview(customerId, entityId, body)
  );

export const useApplyPlanChange = () =>
  useMutation<
    PlanChangeResult,
    AxiosError<any>,
    Scope & {
      from_plan: string;
      to_plan: string;
      proration_date: number | string;
      request_id: string;
    }
  >(({ customerId, entityId, ...body }) =>
    PlanChangeService.apply(customerId, entityId, body)
  );

export const useSaveBillingAddress = () =>
  useMutation<
    unknown,
    AxiosError<any>,
    Scope & { from_plan: string; billing_address: BillingAddress }
  >(({ customerId, entityId, ...body }) =>
    PlanChangeService.saveBillingAddress(customerId, entityId, body)
  );

export const useCancelPlanChange = () =>
  useMutation<PlanChange, AxiosError<any>, Scope & { id: string }>(
    ({ customerId, entityId, id }) =>
      PlanChangeService.cancel(customerId, entityId, id),
    {
      onSettled: (_data, _err, { entityId }) => {
        forgetPlanChanges();
        forgetOwnerPlans(entityId);
      },
      onError: (err) => {
        toast.error(refusalToast(err));
      },
    }
  );

export const useResumeSubscription = () =>
  useMutation<unknown, AxiosError<any>, Scope & { plan: string }>(
    ({ customerId, entityId, plan }) =>
      PlanChangeService.resumeSubscription(customerId, entityId, plan),
    {
      onSettled: (_data, _err, { entityId }) => {
        forgetOwnerPlans(entityId);
        forgetPlanChanges();
      },
      onError: (err) => {
        toast.error(refusalToast(err));
      },
    }
  );
